import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { styleSizesNumber } from 'constants/styleSizes';
import { useLockBodyScroll } from './useLockBodyScroll';
import { useSwipeToToggle } from './useSwipeToToggle';

const focusableSelector = 'a[href], button, input, select, textarea, [tabindex], [contenteditable]';

/** Presentation only: one mounted summary, native sticky scrolling and a manual overlay. */
export const useMobileBookingSummary = () => {
  const [isOverlay, setIsOverlay] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<HTMLDivElement>(null);
  const slotRef = useRef<HTMLDivElement>(null);
  const detailsRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef(isOverlay);
  overlayRef.current = isOverlay;
  const measureRef = useRef<() => void>(() => {});
  const peekRef = useRef(0);
  const flowRef = useRef(false);
  const panelScroll = useRef(0);
  const suppressClickUntil = useRef(0);

  useLockBodyScroll(isOverlay);

  const open = useCallback(() => {
    if (window.innerWidth > styleSizesNumber.miniBreakpoint || overlayRef.current) return;
    measureRef.current();
    if (flowRef.current) return;
    setIsOverlay(true);
  }, []);

  const close = useCallback(() => {
    if (panelRef.current) panelScroll.current = panelRef.current.scrollTop;
    setIsOverlay(false);
  }, []);

  const swipeUp = useCallback(() => {
    suppressClickUntil.current = Date.now() + 400;
    open();
  }, [open]);

  const swipeDown = useCallback(() => {
    suppressClickUntil.current = Date.now() + 400;
    if (overlayRef.current) {
      close();
      return;
    }
  }, [close]);

  useSwipeToToggle(handleRef, swipeUp, swipeDown);

  const toggle = useCallback(() => {
    if (Date.now() < suppressClickUntil.current) {
      suppressClickUntil.current = 0;
      return;
    }
    if (overlayRef.current) close();
    else open();
  }, [close, open]);

  useLayoutEffect(() => {
    const panel = panelRef.current;
    const handle = handleRef.current;
    const slot = slotRef.current;
    const details = detailsRef.current;
    if (!panel || !handle || !slot || !details) return;
    const media = window.matchMedia(`(max-width: ${styleSizesNumber.miniBreakpoint}px)`);
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const viewport = window.visualViewport;
    const tabIndexes = new Map<HTMLElement, string | null>();
    let frame = 0;
    let hidden = false;
    let needsMeasurement = false;
    let flowLayout = false;
    let hasMeasured = false;
    let fadeTimer: number | undefined;
    let titleAnimation: Animation | undefined;

    const cancelTransition = () => {
      window.clearTimeout(fadeTimer);
      fadeTimer = undefined;
      titleAnimation?.cancel();
      titleAnimation = undefined;
      panel.classList.remove('summaryFadingOut');
    };

    const clearStyles = () => {
      ['--summary-height', '--summary-peek-height', '--summary-header-height', '--summary-viewport-inset', '--summary-viewport-height']
        .forEach(property => panel.style.removeProperty(property));
      slot.style.removeProperty('--summary-slot-height');
    };

    const restoreDetails = () => {
      details.removeAttribute('inert');
      details.removeAttribute('aria-hidden');
      tabIndexes.forEach((value, element) => {
        if (value === null) element.removeAttribute('tabindex');
        else element.setAttribute('tabindex', value);
      });
      tabIndexes.clear();
      hidden = false;
    };

    const updateVisibility = () => {
      const bottom = viewport ? viewport.offsetTop + viewport.height : window.innerHeight;
      const shouldHide = media.matches && !overlayRef.current && !flowRef.current && details.getBoundingClientRect().top >= bottom - 1;
      handle.setAttribute('aria-expanded', String(!shouldHide));
      if (!shouldHide) {
        if (hidden) restoreDetails();
        return;
      }
      hidden = true;
      if (details.contains(document.activeElement)) handle.focus({ preventScroll: true });
      details.setAttribute('inert', '');
      details.setAttribute('aria-hidden', 'true');
      // Fallback for browsers without native inert; restore the original tabindex values.
      details.querySelectorAll<HTMLElement>(focusableSelector).forEach(element => {
        if (!tabIndexes.has(element)) tabIndexes.set(element, element.getAttribute('tabindex'));
        if (element.getAttribute('tabindex') !== '-1') element.setAttribute('tabindex', '-1');
      });
    };

    const isInFlow = () => {
      const bottom = viewport ? viewport.offsetTop + viewport.height : window.innerHeight;
      // The slot never moves with sticky. Keep the compact peek as the invariant boundary.
      return media.matches && !overlayRef.current && slot.getBoundingClientRect().top < bottom - peekRef.current - 1;
    };

    const updateFlowMode = () => {
      const inFlow = isInFlow();
      const changed = inFlow !== flowRef.current;
      flowRef.current = inFlow;
      if (!inFlow) {
        const layoutChanged = flowLayout;
        flowLayout = false;
        cancelTransition();
        panel.classList.remove('summaryInFlow');
        return changed || layoutChanged;
      }
      if (flowLayout) {
        panel.classList.add('summaryInFlow');
        return changed;
      }
      if (reducedMotion.matches || !hasMeasured) {
        cancelTransition();
        flowLayout = true;
        panel.classList.add('summaryInFlow');
        return true;
      }
      panel.classList.add('summaryFadingOut');
      if (fadeTimer === undefined) {
        fadeTimer = window.setTimeout(() => {
          fadeTimer = undefined;
          // Recheck geometry before finishing, including a quick reverse scroll.
          if (!isInFlow()) {
            measureRef.current();
            return;
          }
          flowLayout = true;
          panel.classList.remove('summaryFadingOut');
          panel.classList.add('summaryInFlow');
          measureRef.current();
          if (!flowLayout || !flowRef.current) return;
          const title = panel.querySelector<HTMLElement>('.flowSummaryTitle');
          if (!reducedMotion.matches && title?.animate) {
            titleAnimation = title.animate([
              { opacity: 0 },
              { opacity: 1 },
            ], { duration: 250, easing: 'ease-in-out' });
            titleAnimation.onfinish = () => { titleAnimation = undefined; };
          }
        }, 300);
      }
      return changed;
    };

    const measurePanel = () => {
      const styles = window.getComputedStyle(panel);
      const paddingBottom = parseFloat(styles.paddingBottom) || 0;
      const panelTop = panel.getBoundingClientRect().top;
      // Recover natural content height even while the overlay has a capped viewport.
      const contentBottom = Array.from(panel.children).reduce((bottom, child) => {
        const element = child as HTMLElement;
        if (!element.getClientRects().length) return bottom;
        return Math.max(bottom, element.getBoundingClientRect().bottom - panelTop + panel.scrollTop + (parseFloat(window.getComputedStyle(element).marginBottom) || 0));
      }, 0);
      const height = overlayRef.current
        ? contentBottom + paddingBottom + (parseFloat(styles.borderBottomWidth) || 0)
        : panel.getBoundingClientRect().height;
      panel.style.setProperty('--summary-height', `${height}px`);
      panel.style.setProperty('--summary-peek-height', `${peekRef.current}px`);
      slot.style.setProperty('--summary-slot-height', `${height}px`);
      const visibleBottom = viewport ? viewport.offsetTop + viewport.height : window.innerHeight;
      panel.style.setProperty('--summary-viewport-inset', `${Math.max(0, window.innerHeight - visibleBottom)}px`);
      panel.style.setProperty('--summary-viewport-height', `${viewport ? viewport.height : window.innerHeight}px`);
      updateVisibility();
    };

    const measure = () => {
      if (!media.matches) {
        cancelTransition();
        flowLayout = false;
        hasMeasured = false;
        flowRef.current = false;
        panel.classList.remove('summaryInFlow');
        clearStyles();
        restoreDetails();
        return;
      }
      // Measure the compact layout without painting it, even when the flow header is shown.
      // Measuring the flow header instead would move the boundary and cause oscillation.
      panel.classList.remove('summaryInFlow');
      peekRef.current = details.getBoundingClientRect().top - panel.getBoundingClientRect().top + panel.scrollTop;
      const panelStyles = window.getComputedStyle(panel);
      const title = details.querySelector<HTMLElement>('.flowSummaryTitle');
      let titleSpace = 0;
      if (title && title.getClientRects().length) {
        const titleStyles = window.getComputedStyle(title);
        titleSpace = title.getBoundingClientRect().height
          + (parseFloat(titleStyles.marginTop) || 0)
          + (parseFloat(titleStyles.marginBottom) || 0);
      }
      // Replace the whole compact header, including its spacing and any existing title.
      // Content following the title keeps the same offset in both layouts.
      const headerHeight = peekRef.current
        - (parseFloat(panelStyles.borderTopWidth) || 0)
        - (parseFloat(panelStyles.paddingTop) || 0)
        + titleSpace;
      panel.style.setProperty('--summary-header-height', `${Math.max(0, headerHeight)}px`);
      updateFlowMode();
      measurePanel();
      hasMeasured = true;
    };
    measureRef.current = measure;
    const scheduleFrame = () => {
      if (!frame) frame = window.requestAnimationFrame(() => {
        frame = 0;
        if (needsMeasurement) {
          needsMeasurement = false;
          measure();
        } else if (updateFlowMode()) measurePanel();
        else updateVisibility();
      });
    };
    const schedule = () => {
      needsMeasurement = true;
      scheduleFrame();
    };
    const resizeObserver = new ResizeObserver(schedule);
    resizeObserver.observe(panel);
    resizeObserver.observe(details);
    resizeObserver.observe(handle);
    const controls = panel.querySelector('.controls');
    if (controls) resizeObserver.observe(controls);
    const mutationObserver = new MutationObserver(schedule);
    mutationObserver.observe(panel, { childList: true, subtree: true, characterData: true });
    const onBreakpoint = () => {
      if (!media.matches) setIsOverlay(false);
      measure();
    };
    const onTouchStart = () => { suppressClickUntil.current = 0; };
    handle.addEventListener('touchstart', onTouchStart, { passive: true });
    media.addListener(onBreakpoint);
    const onMotionChange = () => {
      if (reducedMotion.matches) cancelTransition();
      measure();
    };
    reducedMotion.addListener(onMotionChange);
    window.addEventListener('resize', schedule);
    window.addEventListener('scroll', scheduleFrame, { passive: true });
    viewport?.addEventListener('resize', schedule);
    viewport?.addEventListener('scroll', schedule);
    measure();
    return () => {
      window.cancelAnimationFrame(frame);
      cancelTransition();
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      media.removeListener(onBreakpoint);
      reducedMotion.removeListener(onMotionChange);
      handle.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('resize', schedule);
      window.removeEventListener('scroll', scheduleFrame);
      viewport?.removeEventListener('resize', schedule);
      viewport?.removeEventListener('scroll', schedule);
      restoreDetails();
      panel.classList.remove('summaryInFlow');
      flowRef.current = false;
      clearStyles();
      measureRef.current = () => {};
    };
  }, [close]);

  useLayoutEffect(() => {
    // Runs after body unlock and after React has restored the flow styles.
    measureRef.current();
  });

  useLayoutEffect(() => {
    if (panelRef.current) panelRef.current.scrollTop = isOverlay ? panelScroll.current : 0;
  }, [isOverlay]);

  return { isOverlay, panelRef, handleRef, slotRef, detailsRef, toggle, close };
};
